import React, { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { formatContactMethodLabel } from "../utils/contactMethodLabel.js"
import OrderInformation from "./OrderInformation.jsx"
import OrderNotes from "./OrderNotes.jsx"
import OrderFileManager from "./OrderFileManager.jsx"
import ShipmentOrderNotes from "./ShipmentOrderNotes.jsx"
import OrderStatus from "./OrderStatus.jsx"
import ProcessBoxUpTable from "./ProcessBoxUpTable.jsx"
import ProcessInformation from "./ProcessInformation.jsx"
import ShippingBillingInfo from "./ShippingBillingInfo.jsx"
import Toast from "../common/Toast.jsx"
import EditManifestModal from "./EditManifestModal.jsx"
import useIsMobileViewport from "../hooks/useIsMobileViewport.js"
import { viewPrintOrder } from "../utils/viewPrintOrder.js"
import { BoxLabels, BoxLabelsProgressModal } from "../box-labels/index.js"
import OrderTimelineSlideout from "./OrderTimelineSlideout.jsx"
import DeleteOrderModal from "./DeleteOrderModal.jsx"
import Button from "../common/Button.jsx"
import { ContactService } from "../services/contactService.js"
import { getOrderOriginLocationId, getPickupLocations } from "../utils/locations.js"

function formatDateOnly(value) {
    return value?.split?.("T")?.[0] ?? "—"
}

function getContact(order) {
    if (!order) {
        return {}
    }
    return order.bill_same_as_ship || !order.bill_to ? order.ship_to ?? {} : order.bill_to ?? {}
}

function getLocationName(id, locations) {
    if (!id) {
        return "—"
    }
    const loc = locations.find((x) => x.id === id)
    return loc?.name ?? "—"
}

function getPickUpLocationName(pickUpLocationId, locations) {
    if (!pickUpLocationId) {
        return "—"
    }
    const loc = locations.find((x) => x.id === pickUpLocationId)
    return loc?.pick_up_name ?? loc?.name ?? "—"
}

function getShippingLocation(order, locations, helpers) {
    if (!order) {
        return "—"
    }
    if (order?.pick_up_location_id) {
        return getLocationName(order.pick_up_location_id, locations)
    }
    const current = helpers?.getCurrentLocation?.(order, locations)
    return current?.name ?? "—"
}

function mapShipToBlock(shipTo, helpers) {
    const s = shipTo ?? {}
    return {
        name: `${s.first_name ?? ""} ${s.last_name ?? ""}`.trim() || "—",
        address: {
            street: s.street_address ?? "—",
            city: s.city ?? "—",
            province: s.state ?? "—",
            country: s.country ?? "—",
            postalCode: s.zip ?? "—",
        },
        phone: (helpers?.formatPhone?.(s.phone) ?? "") || "—",
    }
}

function mapOrderDetails(order, manifest, locations, species, processes, helpers) {
    const contact = getContact(order)
    const primaryShipment = order?.shipments?.[0] ?? {}
    const processingRows = (order?.details ?? []).map((detail) => {
        const speciesName = species.find((x) => x.id === detail.species_id)?.name
        const processName = processes.find((x) => x.id === detail.process_id)?.name
        return {
            species: speciesName ?? detail.other_species_name ?? "Unknown",
            method: processName ?? "Unknown Process",
            quantity: String(detail.num_fish ?? 0),
        }
    })

    return {
        status: helpers?.formatStatus?.(order?.status ?? "") ?? (order?.status ?? ""),
        master: order?.master ?? "—",
        manifestId: order?.manifest_id ?? null,
        manifest: manifest?.manifest_num ? `#${manifest.manifest_num}` : "—",
        lodgeId: order?.lodge_id ? String(order.lodge_id) : "",
        lodge: getLocationName(order?.lodge_id, locations),
        depot: getLocationName(order?.location_id, locations),
        isPickup: Boolean(order?.pick_up_location_id),
        pickUpAtId: order?.pick_up_location_id ? String(order.pick_up_location_id) : "",
        pickUpAt: getPickUpLocationName(order?.pick_up_location_id, locations),
        orderDate: formatDateOnly(order?.ordered_at),
        confirmationEmail: order?.confirm_email_at ? "Sent" : "Not sent",
        preferredContactMethod: formatContactMethodLabel(order?.contact_method),
        isStaff: Boolean(order?.is_staff),
        staffInitial: order?.staff_initial ?? "—",
        tripCode: order?.trip_code ?? "—",
        fishTag: order?.fish_tag ?? "—",
        siNumber: order?.st_jeans_num ?? "—",
        salesOrder: order?.so_num ?? "—",
        shipment: primaryShipment?.shipment_num ?? "TBD",
        invoice: primaryShipment?.invoice_num ?? "TBD",
        totalWeight: order?.total_weight_lb ?? 0,
        numberOfContainers: order?.total_boxes ?? 0,
        frozenShipDate: formatDateOnly(order?.fz_ship_date),
        processes: processingRows,
        shipping: order?.pick_up_location_id ? {
            isPickup: true,
            pickupStore: getPickUpLocationName(order.pick_up_location_id, locations),
            location: getShippingLocation(order, locations, helpers),
        } : {
            isPickup: false,
            ...mapShipToBlock(order?.ship_to, helpers),
        },
        billing: {
            name: `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim() || "—",
            address: {
                street: contact.street_address ?? "—",
                city: contact.city ?? "—",
                province: contact.state ?? "—",
                country: contact.country ?? "—",
                postalCode: contact.zip ?? "—",
            },
            phone: (helpers?.formatPhone?.(contact.phone) ?? "") || "—",
        },
        notes: order?.staff_notes ?? "",
        weighInNotes: order?.weigh_in_notes ?? "",
    }
}

function mapBoxUpLines(lines, helpers) {
    return lines.map((line) => {
        const description = line.description ?? ""
        const [speciesLabel, methodLabel] = description.split(":")
        const inWeightText = line.weight ? `${line.weight} lbs` : ""
        const scaleText = line.scale ? `${line.scale} lbs` : ""
        const piecesText = line.pieces ? `${line.pieces} Pieces` : ""
        return {
            code: line.mino_process || line.mino_fin_good || "—",
            description: description || "—",
            species: speciesLabel || "",
            method: methodLabel || "",
            fish: "",
            inWeight: inWeightText,
            boxUp: [scaleText, piecesText].filter(Boolean).join(" | "),
            sign: line.sign ?? "—",
            date: line.created_at ? (helpers?.formatDateShort?.(line.created_at) ?? "—") : "—",
            frozen: Boolean(line.frozen),
        }
    })
}

function mapBoxUpBoxes(boxes, boxTypes, tidepoolLoading = false) {
    return boxes.map((box) => {
        const boxType = boxTypes.find((x) => x.id === box.box_type_id)
        const boxName = boxType?.type ?? "Box"
        const boxDescription = boxType?.description ?? ""
        return {
            code: boxName,
            description: boxDescription,
            species: "",
            method: "",
            fish: "",
            inWeight: "",
            boxUp: Number(box.gel_packs) ? `${box.gel_packs} gels` : "",
            sign: box.verified_at ? "VERIFIED" : box.picked_at ? "PICKED" : "",
            date: box.pallet ?? "—",
            frozen: Boolean(box.frozen),
            tidepoolLoading,
        }
    })
}

function isOrderConfirmedForEditLock(order) {
    const raw = String(order?.status ?? "").trim().toLowerCase()
    // Treat "" and "new" as the only unconfirmed states (mirrors lodge bulk confirm logic).
    return raw !== "" && raw !== "new"
}

export default function OrderPage({
    app,
    api,
    helpers,
    order,
    manifest,
    boxUpLines,
    boxUpBoxes,
    tidepoolLoading = false,
    onOrderChange = (_order) => { },
    showWeighInNotes = true,
    showBoxUpTable = true,
    showShipmentNotes = true,
    showCreateSoButton = true,
    orderInformationFieldLayout = undefined,
    lockEditsWhenConfirmed = false,
    editLockedToastMessage = "This order is confirmed and can no longer be edited.",
    renderOrderStatusActions = undefined,
    renderOrderFooterExtra = undefined,
    injectBootstrap = false,
    disableManifestEditWhenOffline = false,
}) {
    const navigate = useNavigate()
    const locations = /** @type {any[]} */ (app.locations ?? [])
    const species = /** @type {any[]} */ (app.species ?? [])
    const processes = /** @type {any[]} */ (app.processes ?? [])
    const boxTypes = /** @type {any[]} */ (app.boxTypes ?? [])
    const lodgeOptions = useMemo(() => {
        const lodges = locations
            .filter((location) => Boolean(location.is_lodge))
            .sort((a, b) => String(a.name).localeCompare(String(b.name)))
            .map((location) => ({
                value: String(location.id),
                label: location.name,
            }))
        return [{ value: "", label: "Not from lodge" }, ...lodges]
    }, [locations])
    const pickUpAtOptions = useMemo(() => {
        if (!order?.pick_up_location_id) {
            return []
        }
        return getPickupLocations(getOrderOriginLocationId(order), locations)
            .map((location) => ({
                value: String(location.id),
                label: location.pick_up_name ?? location.name ?? "",
            }))
    }, [order, locations])

    const mappedOrder = useMemo(
        () => mapOrderDetails(order, manifest, locations, species, processes, helpers),
        [order, manifest, locations, species, processes, helpers]
    )
    const manifestEditDisabledBecauseOffline = Boolean(disableManifestEditWhenOffline && app?.connected === false)
    const primaryContact = useMemo(() => {
        const first = String(order?.first_name ?? "").trim()
        const last = String(order?.last_name ?? "").trim()
        const name = `${first} ${last}`.trim()
        const company = String(order?.company ?? "").trim()
        const phone = ContactService.formatPhone(String(order?.phone ?? "").trim())
        const email = String(order?.email ?? "").trim()
        const hasAny = Boolean(name || company || phone || email)
        return {
            name,
            company,
            phone,
            email,
            hasAny,
        }
    }, [order?.first_name, order?.last_name, order?.company, order?.phone, order?.email])
    const mappedProcessRows = useMemo(() => mapBoxUpLines(boxUpLines ?? [], helpers), [boxUpLines, helpers])
    const mappedBoxRows = useMemo(
        () => mapBoxUpBoxes(boxUpBoxes ?? [], boxTypes, tidepoolLoading),
        [boxUpBoxes, boxTypes, tidepoolLoading]
    )
    const allRows = [...mappedProcessRows, ...mappedBoxRows]
    const cannedItems = allRows.filter((x) => !x.frozen)
    const frozenItems = allRows.filter((x) => x.frozen)
    const [toastMessage, setToastMessage] = useState("")
    const [showToast, setShowToast] = useState(false)
    const [toastType, setToastType] = useState(
        /** @type {"info" | "warning" | "error" | "success"} */("error")
    )
    const [toastTitle, setToastTitle] = useState("Error")
    const [toastAutoDismissMs, setToastAutoDismissMs] = useState(
        /** @type {number | null} */(null)
    )
    const [toastVersion, setToastVersion] = useState(0)
    const [showEditManifestModal, setShowEditManifestModal] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const isMobile = useIsMobileViewport()
    const [labelsToPrint, setLabelsToPrint] = useState(/** @type {any[]} */([]))
    const [renderHiddenPdf, setRenderHiddenPdf] = useState(false)
    const [showBoxLabelsModal, setShowBoxLabelsModal] = useState(false)
    const [boxLabelProgress, setBoxLabelProgress] = useState(0)
    const cancelBoxLabelsRef = useRef(false)
    const [isTimelineOpen, setIsTimelineOpen] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const editsLocked = Boolean(lockEditsWhenConfirmed && isOrderConfirmedForEditLock(order))
    const panelBaseStyle = {
        flex: "1 1 21rem",
        minWidth: "min(21rem, 100%)",
    }
    const overviewPanelStyle = {
        flex: "2 1 30rem",
        minWidth: "min(30rem, 100%)",
    }

    useEffect(() => {
        console.log("order", order)
    }, [order])

    useEffect(() => {
        if (!showBoxLabelsModal) {
            setRenderHiddenPdf(false)
            setLabelsToPrint([])
            setBoxLabelProgress(0)
        }
    }, [showBoxLabelsModal])

    useEffect(() => {
        if (!showBoxLabelsModal) {
            return
        }
        if (boxLabelProgress !== 100) {
            return
        }
        const timeoutId = window.setTimeout(() => {
            setShowBoxLabelsModal(false)
        }, 500)
        return () => window.clearTimeout(timeoutId)
    }, [boxLabelProgress, showBoxLabelsModal])

    async function handleMasterSave(nextMaster) {
        if (!order?.id) {
            return
        }
        const nextOrder = {
            ...order,
            master: nextMaster,
        }
        const response = await api.updateOrder(order.id, nextOrder)
        const updatedOrder = response?.data ?? response
        onOrderChange(updatedOrder ?? nextOrder)
        showSuccessToast("Master number updated successfully.")
    }

    async function handleLodgeSave(nextLodgeValue) {
        if (!order?.id) {
            return
        }
        const nextLodgeId = nextLodgeValue ? Number(nextLodgeValue) : null
        const nextOrder = {
            ...order,
            lodge_id: nextLodgeId,
        }
        const response = await api.updateOrder(order.id, nextOrder)
        const updatedOrder = response?.data ?? response
        onOrderChange(updatedOrder ?? nextOrder)
        showSuccessToast("Lodge updated successfully.")
    }

    async function handlePickUpAtSave(nextPickUpAtValue) {
        if (!order?.id) {
            return
        }
        const nextPickUpLocationId = nextPickUpAtValue ? Number(nextPickUpAtValue) : null
        const nextOrder = {
            ...order,
            pick_up_location_id: nextPickUpLocationId,
        }
        const response = await api.updateOrder(order.id, nextOrder)
        const updatedOrder = response?.data ?? response
        onOrderChange(updatedOrder ?? nextOrder)
        showSuccessToast("Pick up location updated successfully.")
    }

    function showErrorToast(error) {
        const message = error?.message || String(error) || "An error occurred"
        setToastType("error")
        setToastTitle("Error")
        setToastAutoDismissMs(null)
        setToastMessage(message)
        setToastVersion((x) => x + 1)
        setShowToast(true)
    }

    function showSuccessToast(message) {
        setToastType("success")
        setToastTitle("Success")
        setToastAutoDismissMs(2600)
        setToastMessage(message)
        setToastVersion((x) => x + 1)
        setShowToast(true)
    }

    function showNotImplementedToast(message = "This is not implemented yet.") {
        setToastType("warning")
        setToastTitle("Not implemented")
        setToastAutoDismissMs(2600)
        setToastMessage(message)
        setToastVersion((x) => x + 1)
        setShowToast(true)
    }

    function showEditLockedToast() {
        setToastType("warning")
        setToastTitle("Editing disabled")
        setToastAutoDismissMs(2600)
        setToastMessage(editLockedToastMessage)
        setToastVersion((x) => x + 1)
        setShowToast(true)
    }

    async function handleViewPrintOrder() {
        try {
            await viewPrintOrder({ order, app })
        } catch (error) {
            showErrorToast(error)
        }
    }

    function handlePrintBoxLabels() {
        try {
            if (!order) {
                return
            }
            setBoxLabelProgress(0)
            cancelBoxLabelsRef.current = false
            setShowBoxLabelsModal(true)
            setLabelsToPrint([order])
            window.setTimeout(() => setRenderHiddenPdf(true), 200)
        } catch (error) {
            showErrorToast(error)
        }
    }

    function handleCancelBoxLabels() {
        cancelBoxLabelsRef.current = true
        setRenderHiddenPdf(false)
        setShowBoxLabelsModal(false)
    }

    function handleEditOrder() {
        if (editsLocked) {
            showEditLockedToast()
            return
        }
        const id = order?.id
        if (!id) return
        navigate(`/v2/orders/${id}/edit`)
    }

    async function refreshOrder() {
        if (!order?.id) {
            return
        }
        const latestOrder = await api.getOrder(order.id)
        onOrderChange(latestOrder ?? order)
    }

    async function handleDeleteOrder(staffInitial) {
        if (!order?.id || isDeleting) return
        setIsDeleting(true)
        try {
            const offlineHandle = Boolean(order?.created_offline) ? (order?.id ?? null) : null
            await api.deleteOrder(order.id, String(staffInitial ?? "").trim(), offlineHandle ? { offlineHandle } : undefined)
            showSuccessToast("Order deleted.")
            setShowDeleteModal(false)
            // Keep it simple: hard redirect to orders list.
            window.setTimeout(() => {
                window.location.assign("/v2/orders")
            }, 300)
        } catch (error) {
            showErrorToast(error)
        } finally {
            setIsDeleting(false)
        }
    }

    async function handleOrderNotesSave(nextNotes) {
        if (!order?.id) {
            return
        }
        const nextOrder = { ...order, staff_notes: nextNotes }
        const offlineHandle = Boolean(order?.created_offline) ? (order?.id ?? null) : null
        const response = await api.updateOrder(order.id, nextOrder, offlineHandle ? { offlineHandle } : undefined)
        const updatedOrder = response?.data ?? response
        onOrderChange(updatedOrder ?? nextOrder)
        showSuccessToast("Order notes updated successfully.")
    }

    async function handleWeighInNotesSave(nextNotes) {
        if (!order?.id) {
            return
        }
        const nextOrder = { ...order, weigh_in_notes: nextNotes }
        const offlineHandle = Boolean(order?.created_offline) ? (order?.id ?? null) : null
        const response = await api.updateOrder(order.id, nextOrder, offlineHandle ? { offlineHandle } : undefined)
        const updatedOrder = response?.data ?? response
        onOrderChange(updatedOrder ?? nextOrder)
        showSuccessToast("Weigh in notes updated successfully.")
    }

    async function handleShipmentNotesSave(nextNotes) {
        if (!order?.id) {
            return
        }
        const response = await api.updateShipmentNotes(order.id, {
            new_notes: nextNotes,
            original_notes: order?.shipment_notes ?? "",
        })
        const updatedOrder = response?.data ?? response
        onOrderChange(updatedOrder ?? order)
        showSuccessToast("Shipment notes updated successfully.")
    }

    return (
        <>
            <Toast
                key={toastVersion}
                show={showToast}
                type={toastType}
                title={toastTitle}
                message={toastMessage}
                autoDismissMs={toastAutoDismissMs}
                onClose={() => {
                    setShowToast(false)
                    setToastMessage("")
                }}
            />
            <BoxLabels
                orders={labelsToPrint}
                locations={locations}
                helpers={helpers}
                openPdf={renderHiddenPdf}
                setOpenPdf={setRenderHiddenPdf}
                progress={boxLabelProgress}
                setProgress={setBoxLabelProgress}
                cancelRef={cancelBoxLabelsRef}
                app={app}
            />
            <BoxLabelsProgressModal
                show={showBoxLabelsModal}
                progress={boxLabelProgress}
                onCancel={handleCancelBoxLabels}
            />
            <OrderTimelineSlideout
                order={order}
                boxUpBoxes={boxUpBoxes}
                boxTypes={boxTypes}
                isOpen={isTimelineOpen}
                onClose={() => setIsTimelineOpen(false)}
            />
            <OrderStatus
                status={mappedOrder.status}
                showCreateSoButton={showCreateSoButton}
                onEditOrder={handleEditOrder}
                onNotImplemented={/** @type {any} */ (() => showNotImplementedToast())}
                onViewPrintOrder={/** @type {any} */ (() => handleViewPrintOrder())}
                onPrintBoxLabels={/** @type {any} */ (() => handlePrintBoxLabels())}
                onTimeline={/** @type {any} */ (() => setIsTimelineOpen((v) => !v))}
                order={order}
                renderActions={
                    typeof renderOrderStatusActions === "function" ?
                        () =>
                            renderOrderStatusActions({
                                Button,
                                order,
                                app,
                                api,
                                helpers,
                                refreshOrder,
                                onError: showErrorToast,
                                onSuccess: showSuccessToast,
                            }) :
                        null
                }
            />
            <EditManifestModal
                show={showEditManifestModal && !isMobile}
                onClose={() => setShowEditManifestModal(false)}
                order={order}
                locations={locations}
                api={api}
                helpers={helpers}
                onUpdated={refreshOrder}
                onError={showErrorToast}
                onSuccess={showSuccessToast}
            />
            <DeleteOrderModal
                show={showDeleteModal}
                order={order}
                isDeleting={isDeleting}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteOrder}
            />

            <div className="d-flex flex-wrap gap-4 align-items-stretch">
                <div style={overviewPanelStyle}>
                    <OrderInformation
                        orderDetails={mappedOrder}
                        fieldLayout={orderInformationFieldLayout}
                        onMasterSave={handleMasterSave}
                        onMasterSaveError={showErrorToast}
                        onLodgeSave={handleLodgeSave}
                        onLodgeSaveError={showErrorToast}
                        lodgeOptions={lodgeOptions}
                        onPickUpAtSave={handlePickUpAtSave}
                        onPickUpAtSaveError={showErrorToast}
                        pickUpAtOptions={pickUpAtOptions}
                        pickUpAtDisabled={editsLocked}
                        onEditManifestClick={() => setShowEditManifestModal(true)}
                        enableManifestEdit={!isMobile && !manifestEditDisabledBecauseOffline}
                    />
                </div>
                <div style={panelBaseStyle}>
                    <ShippingBillingInfo
                        order={order}
                        primaryContact={primaryContact}
                        shipping={mappedOrder.shipping}
                        billing={mappedOrder.billing}
                    />
                </div>
                <div style={panelBaseStyle}>
                    <ProcessInformation orderDetails={mappedOrder} />
                </div>
            </div>

            <div className="order-notes-files-row" style={{ marginTop: "2rem" }}>
                <div>
                    <OrderNotes
                        title="Order Notes"
                        notes={mappedOrder.notes}
                        onStartEdit={() => {
                            if (editsLocked) {
                                showEditLockedToast()
                                return false
                            }
                            return true
                        }}
                        onSave={async (nextNotes) => {
                            if (editsLocked) {
                                showEditLockedToast()
                                return
                            }
                            try {
                                await handleOrderNotesSave(nextNotes)
                            } catch (error) {
                                showErrorToast(error)
                                throw error
                            }
                        }}
                    />
                </div>
                {showWeighInNotes ? (
                    <div>
                        <OrderNotes
                            title="Weigh In Notes"
                            notes={mappedOrder.weighInNotes}
                            placeholder="Notes here will pop up during weigh in"
                            onStartEdit={() => {
                                if (editsLocked) {
                                    showEditLockedToast()
                                    return false
                                }
                                return true
                            }}
                            onSave={async (nextNotes) => {
                                if (editsLocked) {
                                    showEditLockedToast()
                                    return
                                }
                                try {
                                    await handleWeighInNotesSave(nextNotes)
                                } catch (error) {
                                    showErrorToast(error)
                                    throw error
                                }
                            }}
                        />
                    </div>
                ) : null}
                <div>
                    <OrderFileManager
                        orderId={order?.id}
                        files={order?.files}
                        api={api}
                        apiOptions={Boolean(order?.created_offline) ? { offlineHandle: order?.id } : undefined}
                        formatFileSize={helpers?.formatFileSize}
                        onChange={refreshOrder}
                        onError={showErrorToast}
                        onSuccess={showSuccessToast}
                    />
                </div>
            </div>

            {showBoxUpTable ? (
                <div className="bg-warning bg-opacity-10 row g-4 mt-4 pt-4 p-4 p-lg-5">
                    <div className="col-12">
                        <ProcessBoxUpTable cannedItems={cannedItems} frozenItems={frozenItems} />
                    </div>
                </div>
            ) : null}

            {showShipmentNotes ? (
                <div className="mt-4 pb-4">
                    <ShipmentOrderNotes
                        notes={order?.shipment_notes ?? ""}
                        onStartEdit={() => {
                            if (editsLocked) {
                                showEditLockedToast()
                                return false
                            }
                            if (!order?.st_jeans_num) {
                                showErrorToast(new Error("Shipping notes can only be added to orders with a St Jeans number."))
                                return false
                            }
                            return true
                        }}
                        onSave={async (nextNotes) => {
                            if (editsLocked) {
                                showEditLockedToast()
                                return
                            }
                            try {
                                await handleShipmentNotesSave(nextNotes)
                            } catch (error) {
                                showErrorToast(error)
                                throw error
                            }
                        }}
                    />
                </div>
            ) : null}

            <div className="mt-4 pb-5">
                <div className="d-flex justify-content-end gap-2">
                    {typeof renderOrderFooterExtra === "function" ?
                        renderOrderFooterExtra({ Button, order, onOrderChange }) :
                        null}
                    <Button
                        variant="danger"
                        size="sm"
                        className="px-3"
                        disabled={!order?.id || isDeleting}
                        onClick={() => {
                            if (editsLocked) {
                                showEditLockedToast()
                                return
                            }
                            setShowDeleteModal(true)
                        }}
                    >
                        Delete Order
                    </Button>
                </div>
            </div>
        </>
    )
}
